import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Box, Grid, CircularProgress, Typography, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import MenuTree from './MenuTree';
import DetailPanel from './DetailPanel';
import {
  fetchMenuMap,
  fetchMenuId,
  setSelectedEntity,
} from '../../../redux/actions/posMenu';
import { ENTITY_TYPES } from '../../../constants/posMenu';
import { isPosProviderConnected } from '../../../utils/checkAccessDenied';

const PosMenuOverview = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { menuMap, loading, selectedEntity, selectedEntityType } = useSelector(
    (state) => state.posMenu
  );
  const {
    items: providerItems,
    loading: providersLoading,
    providerId,
  } = useSelector((state) => state.providers);

  // Get business ID from localStorage
  const businessId = JSON.parse(localStorage.getItem('businessInfo'))?.id;

  // Check if providers have been loaded (providerId is set when providers are fetched)
  const providersLoaded = providerId !== null || providerItems?.length > 0;

  // Check if POS is connected
  const isPosConnected = isPosProviderConnected(providerItems);

  // Handle URL query parameters for navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get('item');
    const categoryId = searchParams.get('category');
    const newItem = searchParams.get('newItem');

    if (newItem === 'true') {
      // Create a new item - set empty entity to trigger new item form
      dispatch(setSelectedEntity({}, ENTITY_TYPES.ITEM));

      // Clear the query parameter after selecting
      searchParams.delete('newItem');
      history.replace({ search: searchParams.toString() });
    } else if (itemId && menuMap?.items?.[itemId]) {
      const item = menuMap.items[itemId];
      dispatch(setSelectedEntity(item, ENTITY_TYPES.ITEM));

      // Clear the query parameter after selecting
      searchParams.delete('item');
      history.replace({ search: searchParams.toString() });
    } else if (categoryId && menuMap?.categories?.[categoryId]) {
      dispatch(
        setSelectedEntity(menuMap.categories[categoryId], ENTITY_TYPES.CATEGORY)
      );
      // Clear the query parameter after selecting
      searchParams.delete('category');
      history.replace({ search: searchParams.toString() });
    }
  }, [location.search, menuMap, dispatch, history]);

  useEffect(() => {
    if (!businessId) {
      toast.error('No business selected');
      return;
    }

    // First fetch menu ID, then fetch menu map
    dispatch(fetchMenuId(businessId))
      .then((fetchedMenuId) => {
        return dispatch(fetchMenuMap(fetchedMenuId));
      })
      .catch(() => {
        toast.error('Failed to load menu data');
      });
  }, [dispatch, businessId]);

  // Show loading while providers are loading or haven't been fetched yet
  if (providersLoading || !providersLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Only show "POS Not Connected" after providers have finished loading
  if (!isPosConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            POS Not Connected
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            Please connect your business to a POS system to access the POS Menu.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Go to <strong>Providers</strong> to connect your POS system.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (loading && !menuMap) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            borderRight: '1px solid #e0e0e0',
            height: '100%',
            overflow: 'auto',
            bgcolor: '#fafafa',
          }}
        >
          <MenuTree
            menuMap={menuMap}
            selectedEntity={selectedEntity}
            selectedEntityType={selectedEntityType}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          sx={{
            height: '100%',
            overflow: 'auto',
            bgcolor: '#f5f5f5',
          }}
        >
          <DetailPanel
            selectedEntity={selectedEntity}
            selectedEntityType={selectedEntityType}
            menuMap={menuMap}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PosMenuOverview;
